/**
 * Default post-login route by role / page permissions.
 *
 * ris-super-admin lands on /facilities (facility management).
 * ris-admin lands on /admin-dashboard (centre dashboard).
 * Everyone else lands on / (Studies worklist).
 */

import { authService, AuthUser } from '../auth/authService';
import { canAccessPage } from './permissions';
import { hasRole, ROLES } from './roleUtils';

/**
 * Get the default route for the current user based on their role
 * @param user - Optional user object. If not provided, will get from authService
 * @returns The default route path for the user
 */
export function getDefaultRoute(user?: AuthUser | null): string {
  const currentUser = user || authService.getUser();

  if (currentUser && hasRole(currentUser, ROLES.SUPER_ADMIN)) {
    return '/facilities';
  }

  if (currentUser && canAccessPage(currentUser, '/admin-dashboard')) {
    return '/admin-dashboard';
  }

  return '/';
}

/**
 * After auth, apply stored redirect only if the user may open that path.
 * e.g. ris-super-admin must not be sent to /admin-dashboard from session state.
 */
export function resolvePostAuthPathname(
  pathname: string | null | undefined,
  user?: AuthUser | null
): string {
  const currentUser = user ?? authService.getUser();
  const fallback = getDefaultRoute(currentUser);
  const p = pathname || '/';
  if (!p || p === '/') {
    return fallback;
  }
  if (p === '/admin-dashboard' && !canAccessPage(currentUser, '/admin-dashboard')) {
    return fallback;
  }
  return p;
}
